import { toast } from "sonner"
import { useIsMutating } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { HugeiconsIcon } from "@hugeicons/react"
import { CheckmarkCircle02Icon, Loading03Icon } from "@hugeicons/core-free-icons"
import { useChatPanel } from "../hooks/use-chat-panel"
import { useSubmitIntent } from "../hooks/use-submit-intent"
import { UPLOAD_ATTACHMENT_MUTATION_KEY } from "../queries/assistant-queries"

/** Header button: "Submit issue" / "Submit anyway". Visible whenever a
 *  DRAFT exists OR the assistant has replied at least once — so the
 *  submit action is unmissable for the entire life of an unsubmitted
 *  draft. (Earlier behaviour gated this purely on
 *  ``hasAssistantReplied``, which left the button hidden if the user
 *  reopened a draft whose hydration didn't trip the "replied" flag.)
 *  Disabled while a submit-intent turn is streaming. */
export function SubmitIssueButton() {
  const submitMode = useChatPanel((s) => s.submitMode)
  const hasAssistantReplied = useChatPanel((s) => s.hasAssistantReplied)
  const threadStatus = useChatPanel((s) => s.threadStatus)
  const view = useChatPanel((s) => s.view)
  const pendingCount = useChatPanel((s) => s.pendingAttachments.length)
  const pendingRefCount = useChatPanel((s) => s.pendingReferences.length)
  const uploadingCount = useIsMutating({ mutationKey: [...UPLOAD_ATTACHMENT_MUTATION_KEY] })
  const { trigger, isRunning } = useSubmitIntent()

  // Hide when the user is viewing the history list or has opened a thread
  // that is already submitted / abandoned — nothing to submit there.
  if (view === "history") return null
  if (threadStatus && threadStatus !== "DRAFT") return null
  if (!hasAssistantReplied && threadStatus !== "DRAFT") return null

  const isArmed = submitMode === "armed"
  const label = isArmed ? "Submit anyway" : "Submit issue"
  // Three refusal paths — all stage state the user expects to be
  // included that submit-intent doesn't carry:
  // - ``pendingCount > 0`` — user staged uploads but hasn't sent the
  //   chat turn that would link them. Submit-intent doesn't carry
  //   attachment_ids; firing now would file the issue without them.
  // - ``uploadingCount > 0`` — an upload is mid-flight. The file
  //   isn't in pendingAttachments yet (mutation hasn't resolved), so
  //   the staged-count check alone misses it; submitting now would
  //   resolve the upload into a now-closed thread and orphan the file.
  // - ``pendingRefCount > 0`` — same shape: references are persisted
  //   onto a user message during a normal chat turn, not via the
  //   submit endpoint. Submitting now would file the issue without
  //   the order/line-item the user just chipped in.
  const blockedByUploads = pendingCount > 0 || uploadingCount > 0
  const blockedByReferences = pendingRefCount > 0
  const blocked = blockedByUploads || blockedByReferences
  const blockedReason = blockedByUploads
    ? "Wait for uploads to finish, then send your message."
    : "Send your message first to attach those references."

  const handleClick = async () => {
    if (blocked) {
      toast.info(blockedReason)
      return
    }
    try {
      await trigger(isArmed ? "submit_force" : "submit")
    } catch (err) {
      toast.error("Couldn't prepare submission. Please try again.")
      console.error(err)
    }
  }

  return (
    <Button
      variant={isArmed ? "default" : "outline"}
      size="sm"
      className="h-7 cursor-pointer gap-1.5 text-xs"
      onClick={handleClick}
      disabled={isRunning || blocked}
      title={blocked ? blockedReason : undefined}
    >
      <HugeiconsIcon
        icon={isRunning ? Loading03Icon : CheckmarkCircle02Icon}
        size={13}
        className={isRunning ? "animate-spin" : ""}
      />
      {label}
    </Button>
  )
}
